<think>

</think>

import React from 'react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import CallToAction from '@/components/CallToAction';
import ConsultationContactForm from '@/components/ConsultationContactForm';
import ClientIndustries from '@/components/ClientIndustries';
import SEO from '@/components/SEO';

// Import refactored section components
import HeroSection from './HeroSection';
import ServicesSection from './ServicesSection';
import WhyChooseUsSection from './WhyChooseUsSection';
import TestimonialsSection from './TestimonialsSection';
import PricingSection from './PricingSection';

const IndexPage: React.FC = () => {
  // Enhanced Schema Markup
  const schemas = [
    // Local Business Schema
    {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "name": "IT Management Solutions",
      "description": "Proactive IT services, cybersecurity & tech support for small businesses. Local, responsive, and secure.",
      "image": "https://itmgmtsol.com/wp-content/uploads/2025/05/logo1200x630-png.avif",
      "url": "https://itmgmtsol.com",
      "telephone": "+1-877-748-6468",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "24 Ackerman Ave",
        "addressLocality": "Airmont",
        "addressRegion": "NY",
        "postalCode": "10901",
        "addressCountry": "US"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": 41.10187202841158,
        "longitude": -74.10808450502081
      },
      "openingHours": "Mo,Tu,We,Th,Fr 08:00-17:00",
      "priceRange": "$$",
      "sameAs": [
        "https://www.facebook.com/ITManagementSolutions/",
        "https://www.linkedin.com/company/it-management-solutions/"
      ],
      "serviceArea": {
        "@type": "GeoCircle",
        "geoMidpoint": {
          "@type": "GeoCoordinates",
          "latitude": 41.10187202841158,
          "longitude": -74.10808450502081
        },
        "geoRadius": "60"
      },
      "potentialAction": {
        "@type": "ReserveAction",
        "target": {
          "@type": "EntryPoint",
          "urlTemplate": "https://itmgmtsol.com/book",
          "inLanguage": "en-US",
          "actionPlatform": [
            "http://schema.org/DesktopWebPlatform",
            "http://schema.org/MobileWebPlatform"
          ]
        },
        "result": {
          "@type": "Reservation",
          "name": "Book a Consultation"
        }
      }
    },
    
    // FAQ Schema
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "What IT services do you offer for small businesses?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "We offer comprehensive IT services for small businesses including managed IT support, cybersecurity solutions, cloud services, network infrastructure, strategic IT consulting, and specialty services tailored to specific industry needs."
          }
        },
        {
          "@type": "Question",
          "name": "How much do your IT services cost?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Our IT service plans are customized based on your business size, needs, and technology requirements. We offer flexible pricing models including all-inclusive fixed monthly fees and as-needed support options. Contact us for a free consultation to receive a personalized quote."
          }
        },
        {
          "@type": "Question",
          "name": "Do you offer emergency IT support?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes, we provide emergency IT support with rapid response times. Our managed service clients receive priority support with guaranteed response times based on their service level agreement, while we also offer emergency services for businesses who need immediate help with critical issues."
          }
        },
        {
          "@type": "Question",
          "name": "Do you work with specific industries?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "We work with a wide range of industries including healthcare, financial services, legal, manufacturing, professional services, and non-profit organizations. Our team understands the unique compliance requirements and technology needs of different sectors."
          }
        }
      ]
    },
    
    // WebSite Schema
    {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "name": "IT Management Solutions",
      "url": "https://itmgmtsol.com",
      "potentialAction": {
        "@type": "SearchAction",
        "target": "https://itmgmtsol.com/search?q={search_term_string}",
        "query-input": "required name=search_term_string"
      }
    }
  ];

  return (
    <div className="min-h-screen">
      <SEO 
        title="IT Management Solutions | Expert IT Support & Cybersecurity for Small Businesses"
        description="Reliable IT support, proactive cybersecurity, and cost-effective tech management for small businesses. Local, responsive service with 24/7 monitoring. Book a free consultation today."
        canonicalUrl="https://itmgmtsol.com"
        schema={schemas}
        keywords="IT management, managed IT services, cybersecurity, small business IT support, network solutions, cloud services, IT consulting, Airmont NY"
      />
      <Navbar />
      
      {/* Hero Section with Updated H1 and Intro Paragraph */}
      <HeroSection />
      
      {/* Services Section */}
      <ServicesSection />
      
      {/* Why Choose Us Section */}
      <WhyChooseUsSection />
      
      {/* Testimonials Section */}
      <TestimonialsSection />
      
      {/* Industries Section */}
      <section id="industries">
        <ClientIndustries />
      </section>
      
      {/* CTA Section */}
      <CallToAction
        title="Ready to transform your business technology?"
        subtitle="Schedule a free consultation today and discover how we can help eliminate your IT headaches and support your growth."
        buttonText="Book Your Free Consultation"
        buttonLink="#contact"
      />
      
      {/* Pricing Section */}
      <PricingSection />
      
      {/* Contact Form Section */}
      <section id="contact">
        <ConsultationContactForm />
      </section>
      
      <Footer />
    </div>
  );
};

export default IndexPage;
